import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Rating,
  useTheme,
} from '@mui/material';
import {
  Forum as ForumIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { forumAPI, coursesAPI } from '../../services/api';
import { toast } from 'react-toastify';
import TopicCard from '../TopicCard';
import Button from '../UIKit/Button';

const ReviewsSection = ({
  course,
  isEnrolled,
  userRating,
  compact = false,
  onRatingChange,
}) => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation(['courses', 'common']);
  const theme = useTheme();
  
  const [recentTopics, setRecentTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [newReviewTitle, setNewReviewTitle] = useState('');
  const [newReviewText, setNewReviewText] = useState('');
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  
  // Combined rating state
  const [currentRating, setCurrentRating] = useState(userRating || 0);

  // Fetch recent topics for the course
  const fetchRecentTopics = useCallback(async () => {
    if (!course?._id) return;
    
    try {
      setLoadingTopics(true);
      setError(null);
      const response = await forumAPI.getAllTopics({
        courseId: course._id,
        page: 1,
        limit: compact ? 2 : 5,
        sortBy: 'newest'
      });
      
      setRecentTopics(response.data.topics || []);
    } catch (err) {
      console.error('Error fetching recent topics:', err);
      setError(t('courses:courseDetail.errors.loadError'));
      setRecentTopics([]);
    } finally {
      setLoadingTopics(false);
    }
  }, [course?._id, compact, t]);

  useEffect(() => {
    fetchRecentTopics();
  }, [fetchRecentTopics]);
  
  // Sync currentRating with userRating prop
  useEffect(() => {
    setCurrentRating(userRating || 0);
  }, [userRating]);
  
  const handleLikeTopic = useCallback(() => {
    // The TopicCard component handles the API call and UI update
    // We just refetch to ensure data consistency
    fetchRecentTopics();
  }, [fetchRecentTopics]);

  const handleRatingChange = useCallback(async (newRating) => {
    if (!course?._id || !isAuthenticated || !isEnrolled) {
      if (!isAuthenticated) {
        toast.info(t('courses:courseDetail.toasts.ratingLoginRequired') || 'Please log in to rate this course');
      } else if (!isEnrolled) {
        toast.info(t('courses:courseDetail.toasts.ratingEnrollmentRequired') || 'You must be enrolled to rate this course');
      }
      return;
    }

    try {
      await coursesAPI.rate(course._id, newRating);
      
      // Use different message for new vs updated rating
      const messageKey = userRating && userRating > 0 
        ? 'courses:courseDetail.toasts.ratingUpdated' 
        : 'courses:courseDetail.toasts.ratingSaved';
      toast.success(t(messageKey));
      
      // Update local state immediately
      setCurrentRating(newRating);
      
      // Call parent callback to update course data
      if (onRatingChange) {
        onRatingChange(newRating);
      }
    } catch (error) {
      console.error('Error updating rating:', error);
      toast.error(t('courses:courseDetail.toasts.ratingError') || 'Error updating rating');
    }
  }, [course?._id, isAuthenticated, isEnrolled, t, onRatingChange, userRating]);


  // Check if user can participate in discussions
  const canParticipate = isAuthenticated && isEnrolled;
  const hasRated = currentRating > 0;

  // Get access restriction message
  const getAccessMessage = () => {
    if (!isAuthenticated) {
      return t('courses:courseDetail.toasts.lessonLoginRequired');
    }
    if (!isEnrolled) {
      return t('courses:courseDetail.toasts.lessonEnrollRequired');
    }
    return null;
  };

  const accessMessage = getAccessMessage();

  // Handle review submission
  const handleSubmitReview = useCallback(async () => {
    if (!newReviewText.trim() || !newReviewTitle.trim() || currentRating === 0) {
      toast.error(t('courses:courseDetail.ratingsModal.loginToRate'));
      return;
    }

    if (!canParticipate) {
      toast.error(accessMessage);
      return;
    }

    setIsSubmittingReview(true);
    try {
      // Create a new topic for the review
      const reviewData = {
        title: newReviewTitle,
        content: newReviewText,
        course: course._id,
        type: 'review',
        rating: currentRating,
      };

      await forumAPI.createTopic(reviewData);
      toast.success(t('forumPage.topics.topicCreated'));
      
      // Reset form
      setNewReviewTitle('');
      setNewReviewText('');
      
      // Refresh topics
      fetchRecentTopics();
    } catch (error) {
      console.error('Error creating review:', error);
      toast.error(t('forumPage.topics.createError'));
    } finally {
      setIsSubmittingReview(false);
    }
  }, [newReviewTitle, newReviewText, currentRating, canParticipate, accessMessage, course._id, t, fetchRecentTopics]);

  // Handle adding comments to topics
  const handleAddComment = useCallback(async (topicId, commentData) => {
    try {
      await forumAPI.addComment(topicId, commentData);
      toast.success(t('forumPage.comments.commentAdded'));
      // Refresh topics to show new comment
      fetchRecentTopics();
    } catch (err) {
      console.error('Error adding comment:', err);
      toast.error(t('forumPage.comments.commentError'));
    }
  }, [t, fetchRecentTopics]);

  return (
    <Box 
      id="reviews-section"
      sx={{ 
        mt: 4,
        scrollMarginTop: '80px',
      }}
    >
      {/* Section Header */}
      <Typography variant="h5" component="h2" fontWeight="bold" mb={2}>
        {t('courses:courseDetail.courseReviews')}
      </Typography>

      {/* New Review Form */}
      {canParticipate && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              {t('courses:courseDetail.rateCourse')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Rating
                value={currentRating}
                onChange={(event, newValue) => {
                  if (newValue !== null) {
                    setCurrentRating(newValue);
                    handleRatingChange(newValue);
                  }
                }}
                precision={0.5}
                size="large"
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: theme.palette.warning.main,
                  },
                }}
              />
              {!hasRated && (
                <Typography variant="body2" color="warning.main" sx={{ fontWeight: 500 }}>
                  {t('courses:courseDetail.pleaseRateFirst')}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              {t('courses:courseDetail.addReview')}
            </Typography>
            <TextField
              fullWidth
              value={newReviewTitle}
              onChange={(e) => setNewReviewTitle(e.target.value)}
              placeholder={t('courses:courseDetail.reviewTitle')}
              variant="outlined"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
          </Box>

          <TextField
            fullWidth
            multiline
            rows={4}
            value={newReviewText}
            onChange={(e) => setNewReviewText(e.target.value)}
            placeholder={t('courses:courseDetail.writeReview')}
            variant="outlined"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSubmitReview}
              disabled={isSubmittingReview || !newReviewText.trim() || !newReviewTitle.trim() || currentRating === 0}
              sx={{
                borderRadius: '8px',
                px: 3,
                py: 1.5,
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                '&.Mui-disabled': {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              {isSubmittingReview 
                ? t('common:actions.saving') 
                : t('courses:courseDetail.publish')
              }
            </Button>
          </Box>
        </Paper>
      )}

      {/* Topics List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {loadingTopics ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : recentTopics.length > 0 ? (
          recentTopics.map((topic) => (
            <TopicCard
              key={topic._id}
              topic={topic}
              onLike={handleLikeTopic}
              onAddComment={canParticipate ? handleAddComment : () => {
                toast.info(accessMessage);
              }}
            />
          ))
        ) : (
          <Paper
            elevation={0}
            sx={{
              borderRadius: '12px',
              p: 4,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
              backgroundColor: 'background.paper',
            }}
          >
            <ForumIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('courses:courseDetail.noTopics')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {accessMessage || t('courses:courseDetail.discussionDesc')}
            </Typography>
          </Paper>
        )}

      </Box>
    </Box>
  );
};

export default ReviewsSection;
